<template>
  <div class="history-holder" v-if="canEdit">
    <card
      :title="$t('import_history') + ' (' + $t('tools.configurator.' + lineToUnderscore(import_ref)) + ')'"
      id="import-history"
      titleBottomPadding="0"
    >
      <div class="row d-flex justify-content-end" style="width: 100%; padding-bottom: 8px">
        <button class="btn btn-md btn-secondary" @click="goBackToTypeList" style="width: 250px">
          {{ $t("common.back_to_list") }}
        </button>
      </div>
      <div class="d-flex align-items-center" style="width: 100%; padding-bottom: 8px">
        <label class="mb-0" style="margin-right: 12px">
          {{ $t("tools.import.select_number_of_rows") }}
        </label>
        <DxSelectBox v-model="selectedLimit" :items="limitOptions" :search-enabled="false" :width="120" @value-changed="onLimitChange" />
      </div>
      <div v-if="/*(activeApiImportConfig ?? false)*/ false">
        /* API import logic was replaced, this function is not available */
        <div class="api-import-box">
          <div style="width: 200px">
            <label class="api-import-label">{{ $t("tools.import.tracktor_api_import") }}</label>
          </div>
          <div style="width: 200px">
            <date-picker2
              v-model="selectedApiDate"
              :value-type="selectedApiDateInput"
              format="YYYY-MM-DD"
              type="date"
              :input-class="['date-picker-style']"
              @change="onApiDateChanged"
              :disabled-date="(date) => date > new Date()"
            ></date-picker2>
          </div>
          <div style="width: 250px">
            <button class="btn btn-primary api-import-send-btn" @click="sendApiImportRequest" :disabled="sendApiDisabled">
              {{ $t("tools.import.get_data") }}
            </button>
          </div>
        </div>
      </div>
      <file-list ref="importFileList" :data="uploaded_files_history" @importValidatedDataEvent="importValidatedData"
        @live-operation-finished="getUploadedFiles(company)"></file-list>
    </card>
    <ConfirmDxPopup
      :show="showConfirmationDialog"
      @close="cancelValidatedDataImport"
      @confirm="startValidatedDataImport"
      :title="$t('tools.import.transfer_to_live_data')"
      :message="$t('tools.import_validator.transfer_to_live_data_question')"
    ></ConfirmDxPopup>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import FileList from "../import/partials/FileList";
import ConfirmDxPopup from "@/components/Modals/ConfirmDxPopup.vue";
import { mapActions, mapGetters } from "vuex";
import { canDelete, canEdit, permissions } from "@/helpers/Permissions";
import Forbidden from "@/pages/errors/Forbidden";
import { getCalculateDateDaysAgo } from "~/helpers/dateHelper";
import { lineToUnderscore, successMessage, errorMessageList, errorMessage } from "~/helpers/common";
import Cookies from "js-cookie";
import DatePicker2 from "vue2-datepicker";
import DxSelectBox from "devextreme-vue/select-box";

export default {
  name: "ImportHistory",
  data() {
    return {
      uploaded_files_history: this.recentlyUploadedFiles ? this.recentlyUploadedFiles : [],
      intervalId: null, // Store the interval ID here
      import_type: "",
      import_ref: "data-import",
      showConfirmationDialog: false,
      transferData: null,
      historyFetches: [],
      activeApiImportConfig: null,
      selectedApiDate: null,
      selectedApiDateInput: null,
      sendApiDisabled: true,
      selectedLimit: 20,
      limitOptions: [20, 50, 100, 300],
    };
  },
  components: {
    FileList,
    Forbidden,
    ConfirmDxPopup,
    DatePicker2,
    DxSelectBox,
  },
  computed: {
    ...mapGetters({
      recentlyUploadedFiles: "company/recentlyUploadedFiles",
      importConfigurations: "importConfig/importConfigurations",
      importSourceCredentials: "importConfig/importSourceCredentials",
    }),
    config() {
      return {
        permission: permissions.CONFIGURATOR,
      };
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
  },
  methods: {
    ...mapActions({
      getRecentlyUploadedFiles: "company/getRecentlyUploadedFiles",
      importApiCall: "importConfig/importApiCall",
    }),
    getUploadedFiles(company) {
      if (company && company.uuid) {
        const request = this.getRecentlyUploadedFiles({
          company_uuid: company.uuid,
          import_type: this.import_type,
          limit: this.selectedLimit,
          date_from: getCalculateDateDaysAgo(200),
        });
        let trackedRequest;
        trackedRequest = request
          .catch(() => null)
          .finally(() => {
            this.historyFetches = this.historyFetches.filter((fetch) => fetch !== trackedRequest);
          });
        this.historyFetches.push(trackedRequest);
        return trackedRequest;
      }
      return Promise.resolve(null);
    },
    startHistoryPolling() {
      this.stopHistoryPolling();
      this.intervalId = setInterval(() => this.getUploadedFiles(this.company), 20000);
    },
    stopHistoryPolling() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    },
    onLimitChange() {
      this.getUploadedFiles(this.company);
    },
    cancelValidatedDataImport() {
      this.showConfirmationDialog = false;
      this.transferData = null;
      this.$refs.importFileList.setItemToLiveData(null);
    },
    startValidatedDataImport() {
      this.showConfirmationDialog = false;
      if (!this.transferData) {
        return;
      }
      const transfer_settings = {
        import_id: this.transferData.import_id,
        import_type: this.transferData.import_type,
        department_uuid: this.transferData.department_uuid,
        warehouse_uuid: this.transferData.warehouse_uuid,
        season_uuid: this.transferData.season_uuid,
        source_data_type: this.transferData.source_data_type,
        uploaded_file_id: this.transferData.uploaded_file_id,
        session_id: this.transferData.session_id,
        filename: this.transferData.filename,
        additional_data: this.transferData.additional_data,
      };

      this.$refs.importFileList.startLiveOperation("transfer", transfer_settings);
      this.transferData = null;
    },
    importValidatedData(data) {
      this.showConfirmationDialog = true;
      this.transferData = data;
    },
    lineToUnderscore(value) {
      return lineToUnderscore(value);
    },
    goBackToTypeList() {
      this.$router.back();
    },
    sendApiImportRequest() {
      if (!this.selectedApiDate || !this.activeApiImportConfig || !this.activeApiImportConfig.source_config_uuid) {
        errorMessage(this, this.$t("tools.import.api_import_required_error"));
        return;
      }

      const payload = {
        date: this.selectedApiDate,
        import_configuration_uuid: this.activeApiImportConfig.uuid,
      };

      this.sendApiDisabled = true;

      let vm = this;
      this.importApiCall(payload)
        .then((response) => {
          successMessage(vm, response.message);
          vm.getUploadedFiles(vm.company);
        })
        .catch((e) => {
          errorMessageList(vm, e.errors);
        });
    },
    onApiDateChanged() {
      this.sendApiDisabled = false;
    },
  },
  async mounted() {
    if (this.$route.query.import_type_id) {
      this.import_type = this.$route.query.import_type_id;
      this.import_ref = this.$route.query.import_typ_ref;
    }

    let vm = this;
    const dom = await this.$store.dispatch("auth/checkDomain", {
      domain: Cookies.get("domain"),
    });
    this.company = dom;
    vm.getUploadedFiles(dom);
    this.startHistoryPolling();

    if (this.importConfigurations) {
      this.activeApiImportConfig = this.importConfigurations.find(
        (config) =>
          config.import_type === this.import_type &&
          config.is_active === 1 &&
          config.source_type === "API_IMPORT" &&
          config.source_config_uuid,
      );
      /*if(this.activeApiImportConfig){
        this.selectedApiDate = moment().format('YYYY-MM-DD');
      }*/
    }
  },
  beforeDestroy() {
    // Use beforeUnmount() for Vue 3
    this.stopHistoryPolling();
  },
  watch: {
    recentlyUploadedFiles: {
      handler: function (newVal) {
        this.uploaded_files_history = newVal;
      },
      deep: true,
    },
  },
};
</script>
<style scoped>
.api-import-box {
  border: 1px solid lightgray;
  padding: 10px;
  border-radius: 5px;
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 5px;
  width: 750px;
}

.api-import-label {
  font-weight: bold;
  margin-right: 10px;
  margin-top: 2px;
}

.api-import-send-btn {
  margin-left: 30px;
  margin-top: 5px;
}
</style>
